/**
 * PerformanceChart - Interactive line chart for performance metrics over time
 * 
 * Migrated from seo-engine-webtools-customer-module
 */

import { useState, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';
import { Box, Typography, Paper } from '@mui/material';
import { CHART_COLORS } from '@/services/customer/constants';
import { formatCompactNumber, formatDateShort } from '@/services/customer/formatters';
import { DailyAggregatesResponse } from '@/services/customer/performanceApi';

interface Props {
  data?: DailyAggregatesResponse | null;
  onDataPointClick?: (date: string, metric: string, value: number) => void;
}

const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <Paper
      elevation={3}
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
        {formatDateShort(label as string)}
      </Typography>
      {payload.map((entry, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: entry.color,
            }}
          />
          <Typography variant="caption">
            {entry.name}: <strong>{formatCompactNumber(entry.value as number)}</strong>
          </Typography>
        </Box>
      ))}
    </Paper>
  );
};

function PerformanceChart({ data, onDataPointClick }: Props) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  // Use dailyMetrics directly from the aggregates response (already grouped by date)
  const chartData = data?.dailyMetrics || [];

  const handleClick = useCallback((data: any) => {
    if (onDataPointClick && data && data.activePayload) {
      const payload = data.activePayload[0].payload;
      const metric = data.activePayload[0].dataKey;
      onDataPointClick(payload.date, metric, payload[metric]);
    }
  }, [onDataPointClick]);

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart
        data={chartData}
        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        onClick={handleClick}
        onMouseMove={(data) => {
          if (data && data.activeTooltipIndex !== undefined) {
            setActiveIndex(data.activeTooltipIndex);
          }
        }}
        onMouseLeave={() => setActiveIndex(null)}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="date"
          tickFormatter={(value) => formatDateShort(value)}
          tick={{ fontSize: 12 }}
        />
        <YAxis
          yAxisId="left"
          tickFormatter={(value) => formatCompactNumber(value)}
          tick={{ fontSize: 12 }}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          tickFormatter={(value) => formatCompactNumber(value)}
          tick={{ fontSize: 12 }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend 
          wrapperStyle={{ cursor: 'default' }}
          iconType="line"
        />
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="clicks"
          stroke={CHART_COLORS[0]}
          strokeWidth={activeIndex !== null ? 3 : 2}
          dot={{ r: 4, strokeWidth: 2, fill: '#fff' }}
          activeDot={{ r: 6, strokeWidth: 2, cursor: onDataPointClick ? 'pointer' : 'default' }}
          name="Clicks"
          animationDuration={500}
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="impressions"
          stroke={CHART_COLORS[1]}
          strokeWidth={activeIndex !== null ? 3 : 2}
          dot={{ r: 4, strokeWidth: 2, fill: '#fff' }}
          activeDot={{ r: 6, strokeWidth: 2, cursor: onDataPointClick ? 'pointer' : 'default' }}
          name="Impressions"
          animationDuration={500}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default PerformanceChart;
